// Cookie Policy content — Founder content authority, published V1 (U.S.), with the authorized
// factual reconciliations applied: no published localStorage/sessionStorage counts, legacy
// arvexis_wellness_checkins described as legacy member-authored storage only, §7 Origin control
// with no CSRF cookie/token claim, §8 Secure + delivered SameSite=None; Partitioned (no repair
// history or internal counts), §10 Google Fonts disclosed as a live third-party request because
// self-hosting is not confirmed in the current build, and no invented cookie names or durations.
// English is the controlling/source draft; Spanish is substantively equivalent.
const p = (text) => ({ type: 'p', text });
const ul = (items) => ({ type: 'ul', items });

export const COOKIE_CONTENT = {
  en: {
    eyebrow: 'LEGAL',
    title: 'Cookie Policy',
    draftNote: 'Effective Date: October 2, 2026',
    lastUpdated: 'Last Updated: October 2, 2026',
    tocLabel: 'In This Policy',
    tocMobileLabel: 'Table of Contents',
    intro: [
      'This Cookie Policy explains how VANTASIGNAL TECHNOLOGIES LLC (“VantaSignal,” “we,” “us,” or “our”) uses cookies, local storage, session storage, and similar browser/device technologies in connection with Arvexis.',
      'Arvexis is operated by VANTASIGNAL TECHNOLOGIES LLC, a Florida limited liability company.',
      'This Cookie Policy should be read together with our Privacy Policy and Terms of Service.',
    ],
    footerLine: 'Private by default. You control what stays.',
    sections: [
      { id: 'what-this-policy-covers', n: 1, title: 'What This Policy Covers', body: [
        p('Web and app experiences can store small pieces of information in your browser or device so the service can function correctly.'),
        p('These technologies can include:'),
        ul([
          'cookies;',
          'local storage;',
          'session storage;',
          'similar browser/device storage mechanisms.',
        ]),
        p('They may be used for things such as:'),
        ul([
          'keeping you signed in;',
          'preserving preferences;',
          'supporting session continuity;',
          'maintaining feature state;',
          'securing access;',
          'remembering product settings;',
          'temporarily holding content or state needed by the application.',
        ]),
        p('Arvexis currently uses browser/device storage necessary to operate supported product functionality.'),
        p('Some local browser storage can contain member-authored content, so browser/device storage should not be treated as entirely anonymous or purely technical.'),
        p('The inventory below reflects the production implementation as verified on September 30, 2026.'),
      ] },
      { id: 'cookies-we-actually-use', n: 2, title: 'Cookies We Actually Use', body: [
        p('Arvexis currently uses a limited number of first-party cookies necessary to operate the Services.'),
        p('These cookies are used for operational and account-related purposes such as:'),
        ul([
          'authentication or session state;',
          'maintaining a signed-in session;',
          'security-related application behavior;',
          'other strictly necessary operational functions.',
        ]),
        p('Arvexis does not currently use cookies for advertising or cross-site behavioral tracking.'),
        p('Arvexis sets exactly two cookies of its own, both first-party, both marked HttpOnly and Secure, both scoped to the whole site:'),
        ul([
          'access_token — keeps you signed in. Lifetime: 15 minutes.',
          'refresh_token — renews your session so you do not have to re-enter your password. Lifetime: 7 days.',
        ]),
        p('Both are deleted when you sign out.'),
        p('The hosting infrastructure that delivers Arvexis also sets its own operational cookies, which Arvexis does not create and does not use for advertising. On the production domain these are currently __emg_vid (an identifier with a one-year lifetime) and __emg_sid (a short-lived identifier of about thirty minutes), both HttpOnly and Secure.'),
        p('We will not invent or publish cookie names, purposes, lifetimes, or scopes that have not been verified against the production implementation.'),
      ] },
      { id: 'what-we-do-not-currently-use', n: 3, title: 'What We Do Not Currently Use', body: [
        p('Arvexis does not currently use third-party advertising, behavioral advertising, third-party analytics tracking, fingerprinting, or cross-site behavioral tracking technologies based on the current verified product state.'),
        p('This means the current Arvexis product is not using browser technologies for:'),
        ul([
          'behavioral advertising;',
          'ad targeting;',
          'cross-site behavioral tracking;',
          'third-party advertising profiles;',
          'fingerprinting;',
          'third-party analytics SDK tracking.',
        ]),
        p('If Arvexis introduces optional analytics, advertising, marketing, or other tracking technologies in the future, this Policy and the Privacy Policy will be updated as required before those technologies become part of the production service.'),
      ] },
      { id: 'local-storage', n: 4, title: 'Local Storage', body: [
        p('Arvexis uses localStorage to keep certain information on the member’s device/browser.'),
        p('This storage can support functions such as:'),
        ul([
          'preferences;',
          'feature state;',
          'continuity across page loads;',
          'product configuration;',
          'certain locally stored member content.',
        ]),
        p('One legacy key, arvexis_wellness_checkins, is capable of containing member-authored content.'),
        p('No current V1 screen writes new information to that key.'),
        p('Because local storage can contain member content, it should not be described as merely “technical” or anonymous storage.'),
        p('The complete set of localStorage keys Arvexis currently writes is: arvexis_lang, arvexis_lang_notice_shown, arvexis_milestone_3_shown, arvexis_notifications, arvexis_privacy, arvexis_trust_acknowledged, arvexis_user_name, arvexis_voice_enabled, arvexis_welcome_seen, and the legacy arvexis_wellness_checkins.'),
      ] },
      { id: 'session-storage', n: 5, title: 'Session Storage', body: [
        p('Arvexis also uses sessionStorage to support temporary application state during a browser session.'),
        p('Session storage generally persists only for the current browser tab or session.'),
        p('It may be used to support temporary feature state and other short-lived application behavior.'),
        p('The complete set of sessionStorage keys Arvexis currently writes is: arvexis_deletion_intent_at and arvexis_deletion_proof (used only while you are confirming an account deletion), arvexis_milestone_shown_session, and one reflection_seen key per dream reflection you have already been shown.'),
      ] },
      { id: 'authentication-and-session-persistence', n: 6, title: 'Authentication and Session Persistence', body: [
        p('Arvexis may use cookies or browser/device storage to maintain authentication and session state.'),
        p('This allows members, where supported, to remain signed in rather than having to authenticate every time they return to Arvexis.'),
        p('Session information may expire or require reauthentication for reasons such as:'),
        ul([
          'explicit sign-out;',
          'expired or invalid credentials;',
          'password or authentication changes;',
          'security concerns;',
          'account recovery;',
          'session changes;',
          'other circumstances reasonably necessary to protect the account.',
        ]),
        p('Future device-access functionality, such as Face ID or another biometric lock, would operate separately from cookie/browser storage and would be documented according to the actual implementation if introduced.'),
      ] },
      { id: 'security-related-storage', n: 7, title: 'Security-Related Storage and Request Protection', body: [
        p('Arvexis does not currently rely on a CSRF cookie or CSRF token architecture.'),
        p('Authenticated browser requests that change account or member information are protected by an Origin validation control designed to reject unauthorized cross-origin requests.'),
        p('Security-related cookies and browser storage are described only according to verified production behavior.'),
        p('We will not claim that a CSRF token, CSRF cookie, or separate security-cookie category exists unless such a mechanism is actually implemented.'),
      ] },
      { id: 'secure-cookie-configuration', n: 8, title: 'Secure Cookie Configuration', body: [
        p('Arvexis configures its authentication/session cookies with the Secure attribute.'),
        p('The application configures its session-cookie SameSite behavior more restrictively, but the current deployment infrastructure causes the cookie delivered to the browser to be represented as:'),
        p('SameSite=None; Partitioned'),
        p('Authenticated browser requests that change information are additionally protected by the verified Origin validation control described above.'),
        p('This Policy describes the behavior members actually receive through the production delivery path rather than relying only on application-level configuration.'),
        p('The production cookie configuration will be re-verified before final publication.'),
      ] },
      { id: 'essential-storage-vs-optional-storage', n: 9, title: 'Essential Storage vs. Optional Storage', body: [
        p('At present, Arvexis does not use advertising or analytics tracking storage as part of the current verified product.'),
        p('Most current browser/device storage exists to support operation of the product.'),
        p('For storage that is strictly necessary to:'),
        ul([
          'authenticate a member;',
          'preserve session state;',
          'maintain security;',
          'preserve necessary product preferences;',
          'operate requested functionality;',
        ]),
        p('the service may not function correctly if that storage is blocked or deleted.'),
        p('All browser and device storage Arvexis itself uses today is essential to operating the product. Arvexis uses no optional storage of its own: no advertising storage, no analytics storage, no tracking storage.'),
        p('If Arvexis later introduces optional analytics, marketing, advertising, or other non-essential storage, we will evaluate whether consent, notice, or additional controls are required before enabling it.'),
      ] },
      { id: 'google-fonts', n: 10, title: 'Google Fonts', body: [
        p('Arvexis loads its typefaces from Google Fonts. When a page loads, your browser requests those font files from Google, which means Google receives your IP address and basic request information such as your browser and operating system.'),
        p('Google Fonts is used only to display text. Arvexis sets no cookie through it and uses it for no advertising, analytics or tracking purpose.'),
      ] },
      { id: 'managing-browser-and-device-storage', n: 11, title: 'Managing Browser and Device Storage', body: [
        p('You can generally control cookies and browser storage through your browser or device settings.'),
        p('Depending on your browser or device, you may be able to:'),
        ul([
          'block cookies;',
          'delete cookies;',
          'clear local storage;',
          'clear session storage;',
          'restrict site data.',
        ]),
        p('Deleting or blocking storage may:'),
        ul([
          'sign you out;',
          'reset preferences;',
          'remove locally stored product state;',
          'affect certain features;',
          'remove locally stored member content.',
        ]),
        p('Because local storage may contain member content, clearing browser/device storage can remove that locally stored information from the device.'),
      ] },
      { id: 'do-not-track', n: 12, title: 'Do Not Track', body: [
        p('Because the current Arvexis product does not use third-party advertising or cross-context behavioral tracking technologies, Arvexis does not presently rely on a “Do Not Track” signal to disable such systems.'),
        p('If tracking-related technologies are introduced later, we will revisit this section and any applicable opt-out obligations.'),
      ] },
      { id: 'changes-to-this-cookie-policy', n: 13, title: 'Changes to This Cookie Policy', body: [
        p('We may update this Cookie Policy as the Services or browser-storage practices change.'),
        p('The latest version will show an updated effective or “Last Updated” date.'),
        p('Material changes will be communicated using channels actually supported by Arvexis and as required by applicable law.'),
        p('We will not claim that a storage technology exists, is configured in a particular way, or serves a particular purpose unless that statement has been verified against the production implementation.'),
      ] },
      { id: 'contact-us', n: 14, title: 'Contact Us', body: [
        p('Questions about cookies, browser storage, or this Policy may be directed to:'),
        p('VANTASIGNAL TECHNOLOGIES LLC, Miami, Florida, United States'),
        ul([
          'Legal / Privacy: legal@arvexisapp.com',
          'Support: support@arvexisapp.com',
          'General inquiries: hello@arvexisapp.com',
          'Business phone: 786-868-9604',
        ]),
        p('Mailing address: VantaSignal Technologies LLC, 1146 NW 7th Court, #602, Miami, FL 33136, United States'),
      ] },
    ],
  },
  es: {
    eyebrow: 'LEGAL',
    title: 'Política de Cookies',
    draftNote: 'Fecha de vigencia: 2 de octubre de 2026',
    lastUpdated: 'Última actualización: 2 de octubre de 2026',
    tocLabel: 'En Esta Política',
    tocMobileLabel: 'Tabla de Contenido',
    intro: [
      'Esta Política de Cookies explica cómo VANTASIGNAL TECHNOLOGIES LLC (“VantaSignal”, “nosotros”, “nos” o “nuestro”) utiliza cookies, almacenamiento local, almacenamiento de sesión y tecnologías similares de navegador/dispositivo en relación con Arvexis.',
      'Arvexis es operado por VANTASIGNAL TECHNOLOGIES LLC, una compañía de responsabilidad limitada de Florida.',
      'Esta Política de Cookies debe leerse conjuntamente con nuestra Política de Privacidad y nuestros Términos de Servicio.',
    ],
    footerLine: 'Privado por defecto. Tú controlas lo que se conserva.',
    sections: [
      { id: 'what-this-policy-covers', n: 1, title: 'Qué cubre esta Política', body: [
        p('Las experiencias web y de aplicaciones pueden almacenar pequeñas cantidades de información en tu navegador o dispositivo para que el servicio funcione correctamente.'),
        p('Estas tecnologías pueden incluir:'),
        ul([
          'cookies;',
          'almacenamiento local;',
          'almacenamiento de sesión;',
          'mecanismos similares de almacenamiento en navegador/dispositivo.',
        ]),
        p('Pueden utilizarse para funciones como:'),
        ul([
          'mantener tu sesión iniciada;',
          'conservar preferencias;',
          'apoyar la continuidad de la sesión;',
          'mantener el estado de funciones;',
          'proteger el acceso;',
          'recordar configuraciones del producto;',
          'almacenar temporalmente contenido o estado necesario para la aplicación.',
        ]),
        p('Arvexis utiliza actualmente almacenamiento del navegador/dispositivo necesario para operar funciones compatibles del producto.'),
        p('Parte del almacenamiento local del navegador puede contener contenido creado por el miembro, por lo que dicho almacenamiento no debe considerarse completamente anónimo ni puramente técnico.'),
        p('El inventario que figura a continuación refleja la implementación de producción verificada el 30 de septiembre de 2026.'),
      ] },
      { id: 'cookies-we-actually-use', n: 2, title: 'Cookies que realmente utilizamos', body: [
        p('Arvexis utiliza actualmente un número limitado de cookies propias necesarias para operar los Servicios.'),
        p('Estas cookies se utilizan para fines operativos y relacionados con la cuenta, tales como:'),
        ul([
          'autenticación o estado de sesión;',
          'mantenimiento de una sesión iniciada;',
          'comportamiento de seguridad relacionado con la aplicación;',
          'otras funciones operativas estrictamente necesarias.',
        ]),
        p('Arvexis actualmente no utiliza cookies para publicidad ni para rastreo conductual entre sitios.'),
        p('Arvexis establece exactamente dos cookies propias, ambas de origen propio, ambas marcadas como HttpOnly y Secure, y ambas con alcance a todo el sitio:'),
        ul([
          'access_token — mantiene tu sesión iniciada. Duración: 15 minutos.',
          'refresh_token — renueva tu sesión para que no tengas que volver a introducir tu contraseña. Duración: 7 días.',
        ]),
        p('Ambas se eliminan cuando cierras sesión.'),
        p('La infraestructura de alojamiento que entrega Arvexis también establece sus propias cookies operativas, que Arvexis no crea y no utiliza para publicidad. En el dominio de producción, actualmente son __emg_vid (un identificador con una duración de un año) y __emg_sid (un identificador de corta duración, de unos treinta minutos), ambas HttpOnly y Secure.'),
        p('No inventaremos ni publicaremos nombres, propósitos, duraciones o alcances de cookies que no hayan sido verificados contra la implementación de producción.'),
      ] },
      { id: 'what-we-do-not-currently-use', n: 3, title: 'Lo que actualmente no utilizamos', body: [
        p('Arvexis actualmente no utiliza tecnologías de publicidad de terceros, publicidad conductual, rastreo analítico de terceros, fingerprinting ni rastreo conductual entre sitios, según el estado actual verificado del producto.'),
        p('Esto significa que el producto Arvexis actual no utiliza tecnologías del navegador para:'),
        ul([
          'publicidad conductual;',
          'segmentación publicitaria;',
          'rastreo conductual entre sitios;',
          'perfiles publicitarios de terceros;',
          'fingerprinting;',
          'rastreo mediante SDK de analítica de terceros.',
        ]),
        p('Si Arvexis introduce en el futuro analítica opcional, publicidad, marketing u otras tecnologías de rastreo, esta Política y la Política de Privacidad se actualizarán según sea necesario antes de que dichas tecnologías formen parte del servicio de producción.'),
      ] },
      { id: 'local-storage', n: 4, title: 'Almacenamiento local', body: [
        p('Arvexis utiliza localStorage para conservar determinada información en el dispositivo/navegador del miembro.'),
        p('Este almacenamiento puede apoyar funciones como:'),
        ul([
          'preferencias;',
          'estado de funciones;',
          'continuidad entre cargas de página;',
          'configuración del producto;',
          'determinado contenido del miembro almacenado localmente.',
        ]),
        p('Una clave heredada, arvexis_wellness_checkins, puede contener contenido creado por el miembro.'),
        p('Ninguna pantalla actual de V1 escribe información nueva en esa clave.'),
        p('Debido a que el almacenamiento local puede contener contenido del miembro, no debe describirse simplemente como almacenamiento “técnico” o anónimo.'),
        p('El conjunto completo de claves de localStorage que Arvexis escribe actualmente es: arvexis_lang, arvexis_lang_notice_shown, arvexis_milestone_3_shown, arvexis_notifications, arvexis_privacy, arvexis_trust_acknowledged, arvexis_user_name, arvexis_voice_enabled, arvexis_welcome_seen y la clave heredada arvexis_wellness_checkins.'),
      ] },
      { id: 'session-storage', n: 5, title: 'Almacenamiento de sesión', body: [
        p('Arvexis también utiliza sessionStorage para apoyar estado temporal de la aplicación durante una sesión del navegador.'),
        p('El almacenamiento de sesión generalmente persiste únicamente durante la pestaña o sesión actual del navegador.'),
        p('Puede utilizarse para apoyar estado temporal de funciones y otro comportamiento de corta duración dentro de la aplicación.'),
        p('El conjunto completo de claves de sessionStorage que Arvexis escribe actualmente es: arvexis_deletion_intent_at y arvexis_deletion_proof (utilizadas únicamente mientras confirmas la eliminación de una cuenta), arvexis_milestone_shown_session, y una clave reflection_seen por cada reflexión de sueño que ya se te haya mostrado.'),
      ] },
      { id: 'authentication-and-session-persistence', n: 6, title: 'Autenticación y persistencia de sesión', body: [
        p('Arvexis puede utilizar cookies o almacenamiento del navegador/dispositivo para mantener la autenticación y el estado de la sesión.'),
        p('Esto permite que los miembros, cuando sea compatible, permanezcan conectados en lugar de tener que autenticarse cada vez que regresan a Arvexis.'),
        p('La información de sesión puede expirar o requerir una nueva autenticación por motivos como:'),
        ul([
          'cierre de sesión explícito;',
          'credenciales expiradas o inválidas;',
          'cambios de contraseña o autenticación;',
          'preocupaciones de seguridad;',
          'recuperación de cuenta;',
          'cambios de sesión;',
          'otras circunstancias razonablemente necesarias para proteger la cuenta.',
        ]),
        p('Futuras funciones de acceso al dispositivo, como Face ID u otro bloqueo biométrico, funcionarían por separado del almacenamiento de cookies/navegador y se documentarían de acuerdo con la implementación real si fueran introducidas.'),
      ] },
      { id: 'security-related-storage', n: 7, title: 'Almacenamiento relacionado con seguridad y protección de solicitudes', body: [
        p('Arvexis no depende actualmente de una cookie CSRF ni de una arquitectura de token CSRF.'),
        p('Las solicitudes autenticadas del navegador que modifican información de la cuenta o del miembro están protegidas mediante un control de validación de origen diseñado para rechazar solicitudes entre orígenes no autorizadas.'),
        p('Las cookies y el almacenamiento del navegador relacionados con seguridad se describen únicamente de acuerdo con el comportamiento verificado en producción.'),
        p('No afirmaremos que exista un token CSRF, una cookie CSRF o una categoría separada de cookies de seguridad a menos que dicho mecanismo se implemente realmente.'),
      ] },
      { id: 'secure-cookie-configuration', n: 8, title: 'Configuración Secure de las cookies', body: [
        p('Arvexis configura sus cookies de autenticación/sesión con el atributo Secure.'),
        p('La aplicación configura el comportamiento SameSite de sus cookies de sesión de una manera más restrictiva, pero la infraestructura actual de despliegue hace que la cookie entregada al navegador se represente como:'),
        p('SameSite=None; Partitioned'),
        p('Las solicitudes autenticadas del navegador que modifican información también están protegidas por el control verificado de validación de origen descrito anteriormente.'),
        p('Esta Política describe el comportamiento que los miembros reciben realmente a través de la ruta de entrega de producción, en lugar de basarse únicamente en la configuración de la aplicación.'),
        p('La configuración de cookies de producción volverá a verificarse antes de la publicación final.'),
      ] },
      { id: 'essential-storage-vs-optional-storage', n: 9, title: 'Almacenamiento esencial frente a almacenamiento opcional', body: [
        p('Actualmente, Arvexis no utiliza almacenamiento para rastreo publicitario o analítico como parte del producto verificado actual.'),
        p('La mayor parte del almacenamiento actual del navegador/dispositivo existe para apoyar el funcionamiento del producto.'),
        p('Para almacenamiento estrictamente necesario para:'),
        ul([
          'autenticar a un miembro;',
          'conservar el estado de sesión;',
          'mantener la seguridad;',
          'conservar preferencias necesarias del producto;',
          'operar la funcionalidad solicitada;',
        ]),
        p('el servicio puede no funcionar correctamente si dicho almacenamiento se bloquea o elimina.'),
        p('Todo el almacenamiento del navegador y del dispositivo que Arvexis utiliza hoy es esencial para operar el producto. Arvexis no utiliza almacenamiento opcional propio: ni almacenamiento publicitario, ni analítico, ni de rastreo.'),
        p('Si Arvexis introduce posteriormente analítica opcional, marketing, publicidad u otro almacenamiento no esencial, evaluaremos si se requiere consentimiento, aviso o controles adicionales antes de habilitarlo.'),
      ] },
      { id: 'google-fonts', n: 10, title: 'Google Fonts', body: [
        p('Arvexis carga sus tipografías desde Google Fonts. Al cargar una página, tu navegador solicita esos archivos de fuentes a Google, por lo que Google recibe tu dirección IP e información básica de la solicitud, como tu navegador y sistema operativo.'),
        p('Google Fonts se utiliza únicamente para mostrar texto. Arvexis no establece ninguna cookie a través de él y no lo utiliza con fines de publicidad, analítica o rastreo.'),
      ] },
      { id: 'managing-browser-and-device-storage', n: 11, title: 'Administración del almacenamiento del navegador y dispositivo', body: [
        p('Generalmente puedes controlar las cookies y el almacenamiento del navegador mediante la configuración de tu navegador o dispositivo.'),
        p('Dependiendo de tu navegador o dispositivo, puedes tener la capacidad de:'),
        ul([
          'bloquear cookies;',
          'eliminar cookies;',
          'borrar almacenamiento local;',
          'borrar almacenamiento de sesión;',
          'restringir datos del sitio.',
        ]),
        p('Eliminar o bloquear almacenamiento puede:'),
        ul([
          'cerrar tu sesión;',
          'restablecer preferencias;',
          'eliminar estado del producto almacenado localmente;',
          'afectar determinadas funciones;',
          'eliminar contenido del miembro almacenado localmente.',
        ]),
        p('Debido a que el almacenamiento local puede contener contenido del miembro, borrar el almacenamiento del navegador/dispositivo puede eliminar esa información almacenada localmente del dispositivo.'),
      ] },
      { id: 'do-not-track', n: 12, title: 'Do Not Track', body: [
        p('Debido a que el producto Arvexis actual no utiliza tecnologías de publicidad de terceros ni rastreo conductual entre contextos, Arvexis no depende actualmente de una señal “Do Not Track” para desactivar dichos sistemas.'),
        p('Si se introducen tecnologías relacionadas con rastreo posteriormente, revisaremos esta sección y las obligaciones de exclusión aplicables.'),
      ] },
      { id: 'changes-to-this-cookie-policy', n: 13, title: 'Cambios a esta Política de Cookies', body: [
        p('Podemos actualizar esta Política de Cookies a medida que cambien los Servicios o las prácticas de almacenamiento del navegador.'),
        p('La versión más reciente mostrará una fecha de entrada en vigor o de “Última actualización”.'),
        p('Los cambios materiales se comunicarán utilizando canales realmente compatibles con Arvexis y según lo exija la legislación aplicable.'),
        p('No afirmaremos que una tecnología de almacenamiento existe, está configurada de una manera particular o sirve para un propósito específico a menos que esa afirmación haya sido verificada contra la implementación de producción.'),
      ] },
      { id: 'contact-us', n: 14, title: 'Contáctanos', body: [
        p('Las preguntas sobre cookies, almacenamiento del navegador o esta Política pueden dirigirse a:'),
        p('VANTASIGNAL TECHNOLOGIES LLC, Miami, Florida, Estados Unidos'),
        ul([
          'Legal / Privacidad: legal@arvexisapp.com',
          'Soporte: support@arvexisapp.com',
          'Consultas generales: hello@arvexisapp.com',
          'Teléfono comercial: 786-868-9604',
        ]),
        p('Dirección postal: VantaSignal Technologies LLC, 1146 NW 7th Court, #602, Miami, FL 33136, Estados Unidos'),
      ] },
    ],
  },
};
